import * as DomUtils from '../../utils/dom-utils';

export const scrollToComponentModule = angular
  .module('Q.components.scroll-to', [])

  .directive('scrollTo', function ($window, scrollToElement, $timeout) {
    return {
      restrict: 'A',
      link(scope, elem, attrs) {
        // used so we don't replace the history state on page load
        let initialLoad = false;

        elem.on('click', function (event) {
          const target = _getTarget(attrs);

          event.preventDefault();
          if (
            !initialLoad &&
            _isAnchor(target) &&
            $window.history &&
            $window.history.replaceState
          ) {
            $window.history.replaceState(
              {},
              '',
              $window.location.href.replace(/#.*/, '') + target,
            );
          }
          initialLoad = false;

          scrollToElement({
            target,
            scrollTarget: attrs.scrollTarget,
            relativeParent: attrs.relativeParent,
          });

          elem[0].blur();
        });

        const hash = $window.location.hash;

        $timeout(() => {
          // handle page-loads
          const target = _getTarget(attrs);
          if (_isAnchor(target) && hash === target) {
            initialLoad = true;
            $timeout(() => elem.triggerHandler('click'));
          }
        }, 250);
      },
    };

    //// private functions

    function _isAnchor(target) {
      return /^#/.test(target);
    }

    function _getTarget(attrs) {
      let target = attrs.scrollTo;
      if (!target) {
        target = attrs.href;
        if (target) {
          target = target.replace(/.*#/, '#');
        }
      }
      return target;
    }
  })

  .directive('scrollHere', function (scrollToElement, $timeout) {
    return {
      restrict: 'A',
      link: function (scope, elem, attrs) {
        $timeout(() => {
          if (attrs.scrollHere && !scope.$eval(attrs.scrollHere)) {
            return;
          }
          scrollToElement({
            target: elem[0],
            scrollTarget: attrs.scrollTarget,
            relativeParent: attrs.relativeParent,
            offset: attrs.scrollOffset || 0,
            ignoreIfVisible: true,
          });
        }, 250);
      },
    };
  })

  .factory('preserveScroll', function ($timeout) {
    return function (target, changeEvent) {
      let $target = angular.element(DomUtils.querySelector(target));
      const scrollParent = $target.scrollableParent();
      // determine current offset from top of parent frame
      const diff = $target.offsetTop() - scrollParent.scrollTop();

      // enable event to change
      const newTarget = changeEvent();
      $timeout(() => {
        // after a digest cycle, adjust the scroll so we don't appear to have changed
        if (newTarget) {
          target = newTarget;
        }
        // re-look up the node if necessary
        let $target = angular.element(DomUtils.querySelector(target));
        scrollParent.scrollTop($target.offsetTop() - diff);
      });
    };
  })

  .factory('scrollToElement', function ($window, $document, $rootScope, $q) {
    let animFrame;

    /**
     * Scrolls to a target element
     * @param target {string|HTMLElement|angular.element} Element to scroll to (required)
     * @param scrollTarget {string|HTMLElement|angular.element?} Element to scroll. If not provided, determined automatically
     * @param relativeParent {string|HTMLElement|angular.element?} If provided, used to determine the top of the element for relatively positioned elements
     * @param ignoreIfVisible {boolean?} If true, don't scroll if the element is already visible
     * @param offset {number?} If provided, offset the final value by this much
     * @param center {boolean|number?} If true, center the item in the view. If a number, it should be between 0 and 1, and is used to offset the value by that %. .5 is centered, .25 is 1/4 from the top, etc.
     * @returns {*}
     */
    function scrollToElement({
      target,
      scrollTarget,
      relativeParent,
      ignoreIfVisible,
      offset = 0,
      center = false,
    }) {
      if (animFrame) {
        $window.cancelAnimationFrame(animFrame);
        animFrame = null;
      }

      const { $scrollTarget, scrollOffset, scrollRelative, isVisible } =
        _calculateScrollTo(
          target,
          scrollTarget,
          relativeParent,
          offset,
          center,
        );

      if (!$scrollTarget || (ignoreIfVisible && isVisible) || !scrollRelative) {
        return $q.resolve(false);
      }

      const animTime = Math.min(500, Math.abs(scrollRelative));
      const start = Date.now();

      const deferred = $q.defer();

      $rootScope.$broadcast('scrollTo.scrolling', true);
      _animateScroll();

      return deferred.promise;

      //// private functions

      function _animateScroll() {
        const now = Date.now();
        const percentTime = (now - start) / animTime;
        const percentScroll =
          percentTime >= 1
            ? 1
            : 0.5 + Math.cos(percentTime * Math.PI - Math.PI) / 2;
        const currentScrollTo =
          scrollOffset + Math.ceil(percentScroll * scrollRelative);

        $scrollTarget.scrollTop(currentScrollTo);

        // continue until done
        if (percentScroll < 1) {
          animFrame = $window.requestAnimationFrame(_animateScroll);
        } else {
          $rootScope.$broadcast('scrollTo.scrolling', false);
          animFrame = null;
          deferred.resolve(true);
        }
      }
    }

    scrollToElement.query = _query;

    return scrollToElement;

    function _query(node) {
      return angular.isString(node) ? DomUtils.querySelector(node) : node;
    }

    function _calculateScrollTo(
      target,
      scrollTarget,
      relativeParent,
      offset = 0,
      center,
    ) {
      target = _query(target);
      if (!target) return {};

      const $target = angular.element(target);
      const targetHeight = $target.offsetHeight();

      scrollTarget = scrollTarget
        ? _query(scrollTarget)
        : $target.scrollableParent()[0];
      relativeParent = relativeParent || scrollTarget;

      const $scrollTarget = angular.element(scrollTarget);
      const scrollTargetHeight = $scrollTarget.offsetHeight();
      let relativeParentTop = 0;
      if (relativeParent && relativeParent !== $window) {
        relativeParentTop = angular.element(_query(relativeParent)).offsetTop();
      }
      if (offset && angular.isString(offset)) {
        offset = angular.element(_query(offset)).offsetHeight();
      }

      // calculate the final scroll value
      let scrollTo =
        $target.offsetTop() -
        relativeParentTop -
        $scrollTarget.getFixedNodeOffsets();
      if (center && targetHeight < scrollTargetHeight) {
        const centeringOffsetRatio = angular.isNumber(center) ? center : 0.5;
        // try to center the node in the view
        scrollTo -= (scrollTargetHeight - targetHeight) * centeringOffsetRatio;
      }
      scrollTo -= offset;

      const scrollOffset = $scrollTarget.scrollTop();
      const scrollRelative = scrollTo - scrollOffset;
      const targetBottom = scrollTo + targetHeight;
      const scrollTargetBottom = scrollOffset + scrollTargetHeight;
      const isVisible =
        scrollTo > scrollOffset && targetBottom < scrollTargetBottom;

      return {
        scrollTo,
        scrollOffset,
        scrollRelative,
        $scrollTarget,
        isVisible,
      };
    }
  });
